import { ProblemHistory } from "@/lib/types";

// 과목 표시명 매핑
export const ERROR_NOTE_SUBJECT_DISPLAY_NAMES: Record<string, string> = {
  DB: "DB",
  "network-os": "네트워크/OS",
  "sw-dev": "SW개발",
  "sw-design": "SW설계",
  "security-newtech": "보안/신기술",
  sql: "SQL",
  c: "C언어",
  java: "자바",
  python: "파이썬",
};

export const ERROR_NOTE_SCOPE_DISPLAY_NAMES: Record<string, string> = {
  sql: "SQL",
  physicalDBdesign: "물리DB 설계",
  logicDBdesign: "논리DB 설계",
  memoryProcess: "메모리/프로세스",
  ip: "IP",
  network7Layer: "네트워크 7계층",
  shellScript: "shell script",
  test: "테스트",
  interface: "인터페이스",
  appDesign: "앱설계",
  uml: "요구사항",
  ui: "UI",
  methodology: "방법론",
  attackTypes: "공격 유형",
  cryptographyAlgorithm: "암호 알고리즘",
  securityFeature: "보안기능",
  newTech: "신기술 및 장비",
  c: "C언어",
  java: "자바",
  python: "파이썬",
};

export const ERROR_NOTE_CODING_SUBJECTS = new Set(["sql", "c", "java", "python"]);

export const ERROR_NOTE_SCOPE_TO_SUBJECT: Record<string, string> = {
  sql: "sql",
  physicalDBdesign: "DB",
  logicDBdesign: "DB",
  memoryProcess: "network-os",
  ip: "network-os",
  network7Layer: "network-os",
  shellScript: "network-os",
  test: "sw-dev",
  interface: "sw-dev",
  appDesign: "sw-design",
  uml: "sw-design",
  ui: "sw-design",
  methodology: "sw-design",
  attackTypes: "security-newtech",
  cryptographyAlgorithm: "security-newtech",
  securityFeature: "security-newtech",
  newTech: "security-newtech",
  c: "c",
  java: "java",
  python: "python",
};

export interface ErrorNoteScopeStats {
  scope: string;
  totalAttempts: number;
  correctCount: number;
  incorrectCount: number;
  accuracyRate: number;
  problemCount: number;
}

export interface ErrorNoteSubjectStats {
  subject: string;
  scopes: ErrorNoteScopeStats[];
  totalAttempts: number;
  correctCount: number;
  incorrectCount: number;
  accuracyRate: number;
  problemCount: number;
}

export interface ErrorNoteDailyStats {
  date: string;
  totalAttempts: number;
  correctCount: number;
  accuracyRate: number;
}

export interface ErrorNoteTotalStats {
  totalAttempts: number;
  correctCount: number;
  incorrectCount: number;
  accuracyRate: number;
  problemCount: number;
}

function getScopeFromProblemId(problemId: string): string | null {
  const parts = problemId.split("_");
  return parts.length >= 1 ? parts[0] : null;
}

// 통계 탭에 항상 표시할 과목 순서 (데이터 없어도 0% 로 표시).
// sql 누락 시 computeErrorNoteStatistics 의 subjectStatsMap.get("sql") 가 undefined → SQL 통계가
// 통째로 드롭됐다(JCG-2060). 코딩 그룹(sql/c/java/python)에 포함해야 ErrorNoteStatsView "코딩"에 노출.
const ALL_SUBJECTS = [
  "DB",
  "network-os",
  "sw-dev",
  "sw-design",
  "security-newtech",
  "sql",
  "c",
  "java",
  "python",
] as const;

export function computeErrorNoteStatistics(
  histories: ProblemHistory[]
): ErrorNoteSubjectStats[] {
  const scopeStatsMap = new Map<string, ErrorNoteScopeStats>();
  histories.forEach((history) => {
    const scope = getScopeFromProblemId(history.problemId);
    if (!scope || !ERROR_NOTE_SCOPE_TO_SUBJECT[scope]) return;

    const existing: ErrorNoteScopeStats = scopeStatsMap.get(scope) || {
      scope,
      totalAttempts: 0,
      correctCount: 0,
      incorrectCount: 0,
      accuracyRate: 0,
      problemCount: 0,
    };

    history.attempts.forEach((attempt) => {
      existing.totalAttempts++;
      if (attempt.isCorrect) existing.correctCount++;
      else existing.incorrectCount++;
    });

    existing.problemCount++;
    scopeStatsMap.set(scope, existing);
  });

  scopeStatsMap.forEach((stats) => {
    if (stats.totalAttempts > 0) {
      stats.accuracyRate = (stats.correctCount / stats.totalAttempts) * 100;
    }
  });

  const subjectStatsMap = new Map<string, ErrorNoteSubjectStats>();
  // 모든 과목을 기본값으로 미리 채워두기 — 데이터 없어도 0%로 표시
  ALL_SUBJECTS.forEach((subject) => {
    subjectStatsMap.set(subject, {
      subject,
      scopes: [],
      totalAttempts: 0,
      correctCount: 0,
      incorrectCount: 0,
      accuracyRate: 0,
      problemCount: 0,
    });
  });

  scopeStatsMap.forEach((scopeStats, scope) => {
    const subject = ERROR_NOTE_SCOPE_TO_SUBJECT[scope];
    if (!subject) return;

    const existing = subjectStatsMap.get(subject);
    if (!existing) return;

    existing.scopes.push(scopeStats);
    existing.totalAttempts += scopeStats.totalAttempts;
    existing.correctCount += scopeStats.correctCount;
    existing.incorrectCount += scopeStats.incorrectCount;
    existing.problemCount += scopeStats.problemCount;
  });

  const result: ErrorNoteSubjectStats[] = [];
  subjectStatsMap.forEach((stats) => {
    if (stats.totalAttempts > 0) {
      stats.accuracyRate = (stats.correctCount / stats.totalAttempts) * 100;
    }
    stats.scopes.sort((a, b) => a.accuracyRate - b.accuracyRate);
    result.push(stats);
  });

  // 데이터 있는 과목(정답률 낮은 순) → 데이터 없는 과목을 뒤로
  result.sort((a, b) => {
    if (a.totalAttempts === 0 && b.totalAttempts === 0) return 0;
    if (a.totalAttempts === 0) return 1;
    if (b.totalAttempts === 0) return -1;
    return a.accuracyRate - b.accuracyRate;
  });
  return result;
}

export function computeErrorNoteTotalStats(
  statistics: ErrorNoteSubjectStats[]
): ErrorNoteTotalStats {
  const total: ErrorNoteTotalStats = {
    totalAttempts: 0,
    correctCount: 0,
    incorrectCount: 0,
    accuracyRate: 0,
    problemCount: 0,
  };

  statistics.forEach((stat) => {
    total.totalAttempts += stat.totalAttempts;
    total.correctCount += stat.correctCount;
    total.incorrectCount += stat.incorrectCount;
    total.problemCount += stat.problemCount;
  });

  if (total.totalAttempts > 0) {
    total.accuracyRate = (total.correctCount / total.totalAttempts) * 100;
  }

  return total;
}

export function computeErrorNoteDailyStats(
  histories: ProblemHistory[]
): ErrorNoteDailyStats[] {
  if (histories.length === 0) return [];

  const dailyMap = new Map<
    string,
    { totalAttempts: number; correctCount: number }
  >();

  histories.forEach((history) => {
    history.attempts.forEach((attempt) => {
      const date = new Date(attempt.timestamp);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

      const existing = dailyMap.get(dateKey) || {
        totalAttempts: 0,
        correctCount: 0,
      };
      existing.totalAttempts++;
      if (attempt.isCorrect) existing.correctCount++;
      dailyMap.set(dateKey, existing);
    });
  });

  const sorted: ErrorNoteDailyStats[] = Array.from(dailyMap.entries())
    .map(([date, stats]) => ({
      date,
      totalAttempts: stats.totalAttempts,
      correctCount: stats.correctCount,
      accuracyRate:
        stats.totalAttempts > 0
          ? (stats.correctCount / stats.totalAttempts) * 100
          : 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  // 최근 30일만 반환
  return sorted.slice(-30);
}
